// src/features/mensagens/hooks/useMensagemAcoes.js
// ============================================================================
// 🎣 HOOK — Acciones de Mensajes y Conversaciones
// 📦 V4 ENTERPRISE — sendMessage() con Attachments · WhatsApp-Grade Total
// ============================================================================
//
// REGLAS DE ORO:
// 1. TODA acción sobre mensajes/conversaciones pasa por este hook
// 2. Cada acción tiene: validación → ejecución → feedback → rollback
// 3. Soporte Undo (5 seg) para eliminaciones (como Gmail/WhatsApp)
// 4. Bulk actions: seleccionar múltiples y aplicar acción
// 5. Toast notifications integradas (success/error/info)
// 6. V2: userId explícito o resuelto de localStorage — anti-crash total
// 7. V3: sendMessage() — validación → adapter → WhatsApp → Firestore
// 8. V4: sendMessage() — Soporte completo de attachments (imagen/documento)
// ============================================================================

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getMensagensService } from '../api/mensagensService';
import { sendWhatsAppMessage } from '../api/whatsappAdapter';
import {
  validateMensagem,
  validateReenvio,
  validateExclusao,
  validateDestinatario,
} from '../utils/validators';
import {
  MESSAGE_STATUS,
  MESSAGE_ACTIONS,
  PERFORMANCE,
  TOAST,
} from '../utils/constants';

// ============================================================================
// 🎯 HOOK PRINCIPAL
// ============================================================================

/**
 * Hook que centraliza todas las acciones sobre mensajes y conversaciones
 * 
 * @param {Object} options
 * @param {Function} [options.onRefresh] - Callback para refrescar lista después de acción
 * @param {Function} [options.onToast] - Callback para mostrar toast (success/error/info)
 * @param {string} [options.userId] - ID del usuario (opcional, se resuelve de localStorage)
 * @returns {Object} Acciones y estado
 */
export function useMensagemAcoes({
  onRefresh,
  onToast,
  userId: propUserId,
} = {}) {
  // --------------------------------------------------------------------------
  // 📦 ESTADO
  // --------------------------------------------------------------------------
  const [actionInProgress, setActionInProgress] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [undoStack, setUndoStack] = useState([]);

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const undoTimersRef = useRef({});
  const serviceRef = useRef(null);
  const mountedRef = useRef(true);

  // --------------------------------------------------------------------------
  // 🔐 RESOLVER userId (V2 — Ultra Robusto)
  // --------------------------------------------------------------------------

  const resolvedUserId = useMemo(() => {
    if (propUserId) return propUserId;

    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch {}

    try {
      if (window.firebase?.auth?.currentUser?.uid) {
        return window.firebase.auth.currentUser.uid;
      }
    } catch {}

    try {
      const db = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      const users = db.users || [];
      if (users.length > 0 && users[0]?.id) {
        return users[0].id;
      }
    } catch {}

    return null;
  }, [propUserId]);

  // --------------------------------------------------------------------------
  // 🏗️ INICIALIZACIÓN
  // --------------------------------------------------------------------------
  useEffect(() => {
    serviceRef.current = getMensagensService(resolvedUserId || undefined);

    console.log('🔌 [useMensagemAcoes] Servicio inicializado', {
      hasUserId: !!resolvedUserId,
      userId: resolvedUserId ? `${resolvedUserId.slice(0, 8)}...` : 'no resuelto',
    });

    return () => {
      mountedRef.current = false;
      Object.values(undoTimersRef.current).forEach(clearTimeout);
      undoTimersRef.current = {};
    };
  }, [resolvedUserId]);

  // --------------------------------------------------------------------------
  // 🔔 TOAST HELPER (Centralizado)
  // --------------------------------------------------------------------------

  const showToast = useCallback((message, type = TOAST.TYPES.INFO) => {
    if (onToast) {
      onToast({ message, type });
    } else if (typeof window !== 'undefined' && window.showToast) {
      window.showToast(message, type);
    }
  }, [onToast]);

  // ==========================================================================
  // 📤 ENVIAR MENSAJE (V4 ENTERPRISE — Con Attachments)
  // ==========================================================================
  //
  // FLUJO COMPLETO:
  // 1. Validar teléfono y mensaje (o attachments)
  // 2. Sanitizar contenido
  // 3. Si tiene attachments, validarlos
  // 4. Guardar en Firestore (registro con attachments)
  // 5. Abrir WhatsApp vía wa.me (con links de attachments)
  // 6. Si API habilitada, enviar media por Graph API
  // ==========================================================================

  /**
   * Envía un mensaje de WhatsApp (con o sin attachments) y registra en el historial
   * 
   * @param {Object} params
   * @param {string} params.phone - Teléfono del destinatario (formato BR o internacional)
   * @param {string} params.name - Nombre del destinatario
   * @param {string} [params.message] - Contenido del mensaje (puede ser texto o undefined si solo hay attachments)
   * @param {Array} [params.attachments] - Array de attachments [{ type, url, name, size, mimeType, thumbnail }]
   * @param {string} [params.clientId] - ID del cliente en el sistema (opcional)
   * @param {string} [params.template] - ID del template usado (opcional)
   * @returns {Promise<{success: boolean, waUrl: string, messageId?: string, hasAttachments?: boolean}>}
   */
  const sendMessage = useCallback(async ({
    phone,
    name,
    message,
    attachments,
    clientId,
    template,
  }) => {
    // 1. Validar teléfono
    if (!phone || !phone.replace(/\D/g, '')) {
      showToast('Número de telefone é obrigatório', TOAST.TYPES.ERROR);
      return { success: false, waUrl: '', error: 'phone_required' };
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) {
      showToast('Número de telefone inválido', TOAST.TYPES.ERROR);
      return { success: false, waUrl: '', error: 'invalid_phone' };
    }

    // 2. 🔥 V4: Validar que haya contenido (texto o attachments)
    const hasText = message && message.trim().length > 0;
    const hasAttachments = attachments && attachments.length > 0;

    if (!hasText && !hasAttachments) {
      showToast('A mensagem não pode estar vazia', TOAST.TYPES.ERROR);
      return { success: false, waUrl: '', error: 'empty_message' };
    }

    // 3. Validar longitud del texto (si hay)
    if (hasText && message.length > 4096) {
      showToast('Mensagem muito longa (máximo 4096 caracteres)', TOAST.TYPES.ERROR);
      return { success: false, waUrl: '', error: 'message_too_long' };
    }

    // 4. 🔥 V4: Validar attachments (si hay)
    if (hasAttachments) {
      for (const att of attachments) {
        if (!att.url) {
          showToast('Arquivo sem URL de download', TOAST.TYPES.ERROR);
          return { success: false, waUrl: '', error: 'attachment_no_url' };
        }
        if (att.size && att.size > 100 * 1024 * 1024) { // 100MB max
          showToast('Arquivo muito grande (máximo 100MB)', TOAST.TYPES.ERROR);
          return { success: false, waUrl: '', error: 'attachment_too_large' };
        }
      }
    }

    // 5. Sanitizar mensaje (o crear mensaje por defecto para attachments)
    const sanitizedMessage = hasText ? message.trim() : '';

    // 6. Marcar acción en progreso
    const actionId = `send:${Date.now()}`;
    setActionInProgress(actionId);

    try {
      // 7. 🔥 V4: Llamar al adapter con attachments
      const result = await sendWhatsAppMessage({
        to: phone,
        message: sanitizedMessage,
        attachments: hasAttachments ? attachments : undefined,
        clientId: clientId || phone,
        clientName: name || 'Cliente',
      });

      // 8. Feedback de éxito (adaptado según contenido)
      if (hasAttachments && hasText) {
        showToast(
          `Mensagem e ${attachments.length} arquivo(s) enviados para ${name || phone}`,
          TOAST.TYPES.SUCCESS
        );
      } else if (hasAttachments) {
        showToast(
          `${attachments.length} arquivo(s) enviado(s) para ${name || phone}`,
          TOAST.TYPES.SUCCESS
        );
      } else if (template) {
        showToast(
          `Mensagem "${template}" enviada para ${name || phone}`,
          TOAST.TYPES.SUCCESS
        );
      } else {
        showToast(
          `Mensagem enviada para ${name || phone}`,
          TOAST.TYPES.SUCCESS
        );
      }

      // 9. Refrescar lista para mostrar el nuevo mensaje en historial
      onRefresh?.();

      setActionInProgress(null);
      return {
        success: true,
        waUrl: result.waUrl || '',
        messageId: result.messageId || null,
        hasAttachments: result.hasAttachments || false,
        attachmentsCount: result.attachmentsCount || 0,
      };
    } catch (error) {
      console.error('❌ [useMensagemAcoes] Error enviando mensaje:', error);

      // 10. Feedback de error
      showToast(
        error.message || 'Erro ao enviar mensagem. Tente novamente.',
        TOAST.TYPES.ERROR
      );

      setActionInProgress(null);
      return {
        success: false,
        waUrl: '',
        error: error.message || 'send_failed',
      };
    }
  }, [onRefresh, showToast]);

  // ==========================================================================
  // 🗑️ ELIMINAR MENSAJE (Con Undo)
  // ==========================================================================

  const deleteMessage = useCallback(async (messageId) => {
    const service = serviceRef.current;
    if (!service || !messageId) return false;

    const validation = validateExclusao({ type: 'message', id: messageId });
    if (!validation.valid) {
      showToast(validation.error, TOAST.TYPES.ERROR);
      return false;
    }

    try {
      setActionInProgress(`delete:${messageId}`);

      // Guardar mensaje para posible undo
      const messageBackup = await service.getMessageById(messageId);

      // Eliminar
      await service.deleteMessage(messageId);

      // Registrar en stack de undo
      const undoId = `undo_${Date.now()}`;
      setUndoStack(prev => [...prev, {
        id: undoId,
        action: 'delete',
        data: messageBackup,
        messageId,
        expiresAt: Date.now() + 5000,
      }]);

      // Timer para limpiar undo (5 segundos)
      undoTimersRef.current[undoId] = setTimeout(() => {
        setUndoStack(prev => prev.filter(u => u.id !== undoId));
        delete undoTimersRef.current[undoId];
      }, 5000);

      showToast('Mensagem excluída', TOAST.TYPES.SUCCESS);
      onRefresh?.();
      setActionInProgress(null);
      return true;
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error eliminando mensaje:', err);
      showToast(err.message || 'Erro ao excluir mensagem', TOAST.TYPES.ERROR);
      setActionInProgress(null);
      return false;
    }
  }, [onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // 🔄 DESHACER ELIMINACIÓN (Undo)
  // --------------------------------------------------------------------------

  const undoDelete = useCallback(async (undoId) => {
    const undoItem = undoId
      ? undoStack.find(u => u.id === undoId)
      : undoStack[undoStack.length - 1];

    if (!undoItem || undoItem.action !== 'delete' || Date.now() > undoItem.expiresAt) {
      showToast('Não é possível desfazer', TOAST.TYPES.WARNING);
      return false;
    }

    try {
      const service = serviceRef.current;
      const msg = undoItem.data;
      if (!msg || !service) return false;

      // Re-crear el mensaje
      await service.sendMessage({
        clientId: msg.clientId,
        clientName: msg.clientName || 'Desconhecido',
        clientPhone: msg.clientPhone || '',
        message: msg.message || '',
        propertyId: msg.propertyId || null,
        template: msg.template || null,
      });

      // Limpiar timer y stack
      if (undoTimersRef.current[undoItem.id]) {
        clearTimeout(undoTimersRef.current[undoItem.id]);
        delete undoTimersRef.current[undoItem.id];
      }
      setUndoStack(prev => prev.filter(u => u.id !== undoItem.id));

      showToast('Exclusão desfeita', TOAST.TYPES.SUCCESS);
      onRefresh?.();
      return true;
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error deshaciendo:', err);
      showToast('Erro ao desfazer', TOAST.TYPES.ERROR);
      return false;
    }
  }, [undoStack, onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // 🗑️ ELIMINAR CONVERSACIÓN COMPLETA
  // --------------------------------------------------------------------------

  const deleteConversation = useCallback(async (clientId) => {
    const service = serviceRef.current;
    if (!service || !clientId) return 0;

    try {
      setActionInProgress(`deleteConversation:${clientId}`);

      const count = await service.deleteConversation(clientId);

      showToast(
        count > 1
          ? `${count} mensagens excluídas`
          : 'Conversa excluída',
        TOAST.TYPES.SUCCESS
      );

      onRefresh?.();
      setActionInProgress(null);
      return count;
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error eliminando conversación:', err);
      showToast(err.message || 'Erro ao excluir conversa', TOAST.TYPES.ERROR);
      setActionInProgress(null);
      return 0;
    }
  }, [onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // ✅ MARCAR COMO LEÍDO
  // --------------------------------------------------------------------------

  const markAsRead = useCallback(async (messageId) => {
    const service = serviceRef.current;
    if (!service || !messageId) return;

    try {
      await service.markAsRead(messageId);
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error marcando leído:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async (clientId) => {
    const service = serviceRef.current;
    if (!service || !clientId) return 0;

    try {
      setActionInProgress(`markAllRead:${clientId}`);
      const count = await service.markAllAsRead(clientId);

      if (count > 0) {
        showToast(
          `${count} mensagem(ns) marcada(s) como lida(s)`,
          TOAST.TYPES.SUCCESS
        );
      }

      onRefresh?.();
      setActionInProgress(null);
      return count;
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error marcando todo leído:', err);
      setActionInProgress(null);
      return 0;
    }
  }, [onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // 📁 ARCHIVAR CONVERSACIÓN
  // --------------------------------------------------------------------------

  const toggleArchive = useCallback(async (clientId, archive = true) => {
    const service = serviceRef.current;
    if (!service || !clientId) return;

    try {
      setActionInProgress(`archive:${clientId}`);

      if (service.archiveConversation) {
        await service.archiveConversation(clientId, archive);
      }

      showToast(
        archive ? 'Conversa arquivada' : 'Conversa desarquivada',
        TOAST.TYPES.SUCCESS
      );

      onRefresh?.();
      setActionInProgress(null);
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error archivando:', err);
      showToast(err.message || 'Erro ao arquivar', TOAST.TYPES.ERROR);
      setActionInProgress(null);
    }
  }, [onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // ⭐ DESTACAR / FAVORITO
  // --------------------------------------------------------------------------

  const toggleStar = useCallback(async (clientId, star = true) => {
    const service = serviceRef.current;
    if (!service || !clientId) return;

    try {
      if (service.starConversation) {
        await service.starConversation(clientId, star);
      }

      showToast(
        star ? 'Conversa favoritada ⭐' : 'Favorito removido',
        TOAST.TYPES.INFO
      );

      onRefresh?.();
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error favoritando:', err);
      showToast('Erro ao favoritar conversa', TOAST.TYPES.ERROR);
    }
  }, [onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // 🔄 REENVIAR MENSAJE
  // --------------------------------------------------------------------------

  const resendMessage = useCallback(async (originalMessageId, target) => {
    const service = serviceRef.current;
    if (!service || !originalMessageId) return null;

    const validation = validateReenvio({
      messageId: originalMessageId,
      targetClientId: target.clientId,
      targetPhone: target.clientPhone,
    });

    if (!validation.valid) {
      showToast(validation.error, TOAST.TYPES.ERROR);
      return null;
    }

    try {
      setActionInProgress(`resend:${originalMessageId}`);

      const newMessage = await service.resendMessage(originalMessageId, {
        clientId: target.clientId,
        clientName: target.clientName || 'Desconhecido',
        clientPhone: target.clientPhone || '',
      });

      showToast('Mensagem reenviada com sucesso', TOAST.TYPES.SUCCESS);
      onRefresh?.();
      setActionInProgress(null);
      return newMessage;
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error reenviando:', err);
      showToast(err.message || 'Erro ao reenviar mensagem', TOAST.TYPES.ERROR);
      setActionInProgress(null);
      return null;
    }
  }, [onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // 📋 COPIAR TEXTO
  // --------------------------------------------------------------------------

  const copyToClipboard = useCallback(async (text, label = 'Texto') => {
    if (!text) return false;

    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copiado`, TOAST.TYPES.SUCCESS);
      return true;
    } catch {
      // Fallback para navegadores sin clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showToast(`${label} copiado`, TOAST.TYPES.SUCCESS);
      return true;
    }
  }, [showToast]);

  // --------------------------------------------------------------------------
  // 📞 COPIAR TELÉFONO
  // --------------------------------------------------------------------------

  const copyPhone = useCallback(async (phone) => {
    if (!phone) return;
    const digits = phone.replace(/\D/g, '');
    await copyToClipboard(digits, 'Telefone');
  }, [copyToClipboard]);

  // --------------------------------------------------------------------------
  // ✅ SELECCIÓN MÚLTIPLE (BULK ACTIONS)
  // --------------------------------------------------------------------------

  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (next.size === 0) {
          setSelectionMode(false);
        }
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const enterSelectionMode = useCallback(() => {
    setSelectionMode(true);
    setSelectedIds(new Set());
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const selectAll = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  const deleteSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return 0;

    try {
      setActionInProgress('bulkDelete');
      let count = 0;

      for (const id of ids) {
        const success = await deleteMessage(id);
        if (success) count++;
      }

      setSelectedIds(new Set());
      setSelectionMode(false);

      if (count > 0) {
        showToast(
          `${count} mensagem(ns) excluída(s)`,
          TOAST.TYPES.SUCCESS
        );
      }

      onRefresh?.();
      setActionInProgress(null);
      return count;
    } catch (err) {
      console.error('❌ [useMensagemAcoes] Error bulk delete:', err);
      showToast('Erro ao excluir mensagens', TOAST.TYPES.ERROR);
      setActionInProgress(null);
      return 0;
    }
  }, [selectedIds, deleteMessage, onRefresh, showToast]);

  const markSelectedAsRead = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    for (const id of ids) {
      await markAsRead(id);
    }
    setSelectedIds(new Set());
    setSelectionMode(false);
    showToast(`${ids.length} mensagens marcadas como lidas`, TOAST.TYPES.SUCCESS);
    onRefresh?.();
  }, [selectedIds, markAsRead, onRefresh, showToast]);

  // --------------------------------------------------------------------------
  // 📤 RETORNO
  // --------------------------------------------------------------------------

  return {
    // Estado
    actionInProgress,
    selectedIds,
    selectionMode,
    undoStack,

    // 🆕 ENVÍO (V4 con Attachments)
    sendMessage,

    // Acciones individuales
    deleteMessage,
    deleteConversation,
    undoDelete,
    markAsRead,
    markAllAsRead,
    toggleArchive,
    toggleStar,
    resendMessage,
    copyToClipboard,
    copyPhone,

    // Bulk actions
    enterSelectionMode,
    exitSelectionMode,
    toggleSelection,
    selectAll,
    deleteSelected,
    markSelectedAsRead,

    // Helpers
    isSelected: (id) => selectedIds.has(id),
    selectedCount: selectedIds.size,
    hasUndo: undoStack.length > 0,
    latestUndo: undoStack[undoStack.length - 1] || null,
  };
}

// ============================================================================
// 📤 EXPORTACIÓN DEFAULT
// ============================================================================

export default useMensagemAcoes;